const CARD_SELECTOR = '.offhoo-product-card';
const MEDIA_WRAPPER_SELECTOR = '[data-offhoo-product-card-media-wrapper]';
const MEDIA_DATA_SELECTOR = '[data-offhoo-product-card-media]';
const MEDIA_IMAGE_SELECTOR = '[data-offhoo-product-card-image]';
const MEDIA_LINK_SELECTOR = '.offhoo-product-card__image-link';
const MEDIA_CONTROL_SELECTOR = '[data-offhoo-product-card-media-control]';
const OVERLAY_INTERACTIVE_SELECTOR = '.offhoo-product-card__overlay button, .offhoo-product-card__overlay a';

if (!window.__offhooProductCardActionsInitialized) {
  window.__offhooProductCardActionsInitialized = true;

  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  const getMediaItems = (card) => {
    if (card.__offhooMediaItems) return card.__offhooMediaItems;

    const mediaData = card.querySelector(MEDIA_DATA_SELECTOR);
    let raw = '';

    if (mediaData instanceof HTMLTemplateElement) {
      raw = mediaData.innerHTML || mediaData.content?.textContent || '';
    } else if (mediaData?.textContent) {
      raw = mediaData.textContent;
    }

    if (!raw.trim()) {
      card.__offhooMediaItems = [];
      return card.__offhooMediaItems;
    }

    try {
      const items = JSON.parse(raw);
      card.__offhooMediaItems = Array.isArray(items) ? items.filter((item) => item?.src) : [];
    } catch (error) {
      console.warn('[Offhoo] Product card media data could not be parsed.', error);
      card.__offhooMediaItems = [];
    }

    return card.__offhooMediaItems;
  };

  const ensureMediaImage = async (card, index) => {
    const mediaItems = getMediaItems(card);
    const media = mediaItems[index];
    if (!media?.src) return null;

    const existing = card.querySelector(`${MEDIA_IMAGE_SELECTOR}[data-media-index="${index}"]`);
    if (existing instanceof HTMLImageElement) {
      if (!existing.complete) {
        await existing.decode().catch(() => undefined);
      }
      return existing;
    }

    const link = card.querySelector(MEDIA_LINK_SELECTOR);
    if (!(link instanceof HTMLElement)) return null;

    const image = new Image();
    image.className = 'offhoo-product-card__image';
    image.alt = '';
    image.sizes = '(min-width: 990px) 25vw, 50vw';
    image.decoding = 'async';
    image.setAttribute('aria-hidden', 'true');
    image.setAttribute('data-offhoo-product-card-image', '');
    image.dataset.mediaIndex = String(index);
    if (media.width) image.width = Number(media.width);
    if (media.height) image.height = Number(media.height);
    image.src = media.src;

    link.appendChild(image);
    await image.decode().catch(() => undefined);
    return image;
  };

  const warmNearbyMedia = (card) => {
    const mediaItems = getMediaItems(card);
    mediaItems.slice(1, 4).forEach((item, offset) => {
      if (!item?.src) return;
      ensureMediaImage(card, offset + 1);
    });
  };

  const setOverlayTabState = (card, isActive) => {
    const interactiveElements = card.querySelectorAll(OVERLAY_INTERACTIVE_SELECTOR);

    interactiveElements.forEach((element) => {
      if (element.hasAttribute('disabled')) return;

      if (!hoverQuery.matches || isActive) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', '-1');
      }
    });
  };

  const initializeCard = (card) => {
    if (!(card instanceof HTMLElement) || card.dataset.offhooProductCardReady === 'true') return;

    card.dataset.offhooProductCardReady = 'true';
    setOverlayTabState(card, false);
  };

  const initializeCards = (root = document) => {
    root.querySelectorAll(CARD_SELECTOR).forEach(initializeCard);
  };

  const setActiveMedia = (mediaWrapper, nextIndex) => {
    const images = mediaWrapper.querySelectorAll(MEDIA_IMAGE_SELECTOR);
    images.forEach((image) => {
      const index = Number.parseInt(image.dataset.mediaIndex || '-1', 10);
      const isActive = index === nextIndex;
      image.classList.toggle('is-active', isActive);
      image.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
    mediaWrapper.dataset.mediaIndex = String(nextIndex);
  };

  const updateMedia = async (card, direction) => {
    const mediaItems = getMediaItems(card);
    if (mediaItems.length < 2) return;

    const mediaWrapper = card.querySelector(MEDIA_WRAPPER_SELECTOR);
    if (!(mediaWrapper instanceof HTMLElement)) return;

    const currentIndex = Number.parseInt(mediaWrapper.dataset.mediaIndex || '0', 10);
    const safeIndex = Number.isFinite(currentIndex) ? currentIndex : 0;
    const nextIndex = (safeIndex + direction + mediaItems.length) % mediaItems.length;

    await ensureMediaImage(card, nextIndex);
    setActiveMedia(mediaWrapper, nextIndex);

    const prefetchIndex = (nextIndex + direction + mediaItems.length) % mediaItems.length;
    ensureMediaImage(card, prefetchIndex);
  };

  document.addEventListener(
    'click',
    (event) => {
      const control = event.target instanceof Element ? event.target.closest(MEDIA_CONTROL_SELECTOR) : null;
      if (!control) return;

      event.preventDefault();
      event.stopPropagation();

      const card = control.closest(CARD_SELECTOR);
      if (!card) return;

      const action = control.getAttribute('data-offhoo-product-card-media-control');
      updateMedia(card, action === 'previous' ? -1 : 1);
    },
    true
  );

  document.addEventListener('mouseover', (event) => {
    const card = event.target instanceof Element ? event.target.closest(CARD_SELECTOR) : null;
    if (!card) return;

    setOverlayTabState(card, true);
    if (card.dataset.offhooMediaWarmed === 'true') return;
    card.dataset.offhooMediaWarmed = 'true';
    warmNearbyMedia(card);
  });

  document.addEventListener('mouseout', (event) => {
    const card = event.target instanceof Element ? event.target.closest(CARD_SELECTOR) : null;
    if (!card || card.contains(event.relatedTarget)) return;

    setOverlayTabState(card, false);
  });

  document.addEventListener('focusin', (event) => {
    const card = event.target instanceof Element ? event.target.closest(CARD_SELECTOR) : null;
    if (card) setOverlayTabState(card, true);
  });

  document.addEventListener('focusout', (event) => {
    const card = event.target instanceof Element ? event.target.closest(CARD_SELECTOR) : null;
    if (!card) return;

    window.setTimeout(() => {
      if (!card.contains(document.activeElement)) setOverlayTabState(card, false);
    });
  });

  hoverQuery.addEventListener('change', () => initializeCards());

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches(CARD_SELECTOR)) initializeCard(node);
        initializeCards(node);
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeCards();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    initializeCards();
    observer.observe(document.body, { childList: true, subtree: true });
  }
}
